import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { ThemeProvider } from "./components/theme-provider";
import { router } from "./routes";
import { AUTH_REQUIRED_EVENT, ApiError } from "./lib/api/client";
import { clearSessionBreadcrumb } from "./lib/session";
import { LiveStreamProvider } from "./lib/livestream/use-livestream";
import { PreferencesProvider } from "./lib/preferences";
import "./index.css";

// One QueryClient per browser session. The defaults match how joesgrove
// renders: stale-while-revalidate for lists/details, single retry on
// 5xx (with backoff), and explicit opt-out of retries on auth/client
// errors so the user sees the 401/4xx immediately.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          if (error.status === 401 || error.status === 403) return false;
          if (error.status >= 400 && error.status < 500) return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Mutations never silently retry — duplicate writes would corrupt
      // audit timelines and trip optimistic-lock checks unpredictably.
      retry: false,
    },
  },
});

function AuthRedirectListener() {
  // joesgrove's API client dispatches AUTH_REQUIRED_EVENT on any 401.
  // We clear the stale session-presence breadcrumb, then reload the current
  // URL. The server-side auth_session middleware then either serves the
  // logged-out landing (at "/") or 302s to the auth service's /login with a
  // return_to=/auth/callback built from the request. No client-side login URL
  // is hard-coded.
  useEffect(() => {
    let bouncing = false;
    function onAuthRequired() {
      if (bouncing) return;
      if (window.location.pathname.startsWith("/auth/")) return;
      bouncing = true;
      clearSessionBreadcrumb();
      window.location.reload();
    }
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () =>
      window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);
  return null;
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <ThemeProvider defaultTheme="system" storageKey="joesgrove-theme">
      <PreferencesProvider>
        <QueryClientProvider client={queryClient}>
          <LiveStreamProvider>
            <AuthRedirectListener />
            <RouterProvider router={router} />
          </LiveStreamProvider>
        </QueryClientProvider>
      </PreferencesProvider>
    </ThemeProvider>
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}
